import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import NexWordmark from "@/components/NexWordmark";

/**
 * PrivacyPage
 * -----------
 * Public HTML rendering of the LIFE LLC Application Privacy Policy.
 * Served at https://nexengines.com/privacy — used as the privacy URL for
 * Plaid Production access, Stripe applications, and other third-party
 * integrations that require a hosted policy link.
 *
 * Source of truth: /public/legal/Privacy_Policy_LIFE_LLC.pdf (also
 * downloadable from the top of this page for auditors).
 */
const PrivacyPage = () => (
  <div className="min-h-screen bg-[#0B0F19]">
    <div className="border-b border-nex-border bg-black/40 backdrop-blur">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 text-nex-chrome hover:text-nex-gold">
          <ArrowLeft size={14} />
          <NexWordmark size="text-xl" />
        </Link>
        <a
          href="/legal/Privacy_Policy_LIFE_LLC.pdf"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-privacy-pdf"
          className="tick-btn-secondary text-xs !py-2 !px-3"
        >
          Download PDF
        </a>
      </div>
    </div>

    <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck size={20} className="text-nex-gold" />
        <span className="mono-label">// POL-PRV-2026-001</span>
      </div>

      <h1 className="serif-head text-4xl md:text-5xl mb-3">Application Privacy Policy</h1>
      <p className="mono-body text-[12px] uppercase tracking-widest text-nex-chrome mb-8">
        Consumer Financial Data Protection &amp; Plaid Integration Privacy Standard
      </p>

      <div className="card-industrial corner-tick p-6 mb-10 grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <MetaRow label="Organization"       value="Learning Institute for Empowerment, LLC" />
        <MetaRow label="Document Ref"       value="POL-PRV-2026-001" />
        <MetaRow label="Platform Engine"    value="NEXENGINES Custom ERP Platform" />
        <MetaRow label="Effective Date"     value="August 26, 2026" />
        <MetaRow label="Application Scope"  value="LIFE Financial & Trade Empowerment App" />
        <MetaRow label="Plaid Integration"  value="Plaid Link Integration Suite" />
      </div>

      <PolicyBody />

      <div className="mt-16 pt-8 border-t border-nex-border mono-body text-[11px] text-nex-muted">
        The Learning Institute for Empowerment, LLC · Privacy &amp; Security Standard
      </div>
    </div>
  </div>
);

const MetaRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="mono-body text-[9px] uppercase tracking-widest text-nex-muted">{label}</span>
    <span className="text-nex-silver">{value}</span>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="serif-head text-2xl mt-10 mb-3 text-nex-silver">{children}</h2>
);

const P = ({ children }) => (
  <p className="text-nex-chrome leading-relaxed mb-4">{children}</p>
);

const Callout = ({ children }) => (
  <div className="border-l-2 border-nex-gold bg-nex-gold/[0.04] px-5 py-4 my-6 text-nex-silver leading-relaxed text-sm">
    {children}
  </div>
);

const List = ({ children }) => <ul className="space-y-2.5 mb-4 pl-5 list-disc marker:text-nex-gold">{children}</ul>;
const Li = ({ children }) => <li className="text-nex-chrome leading-relaxed">{children}</li>;

const PolicyBody = () => (
  <>
    <H2>1. Introduction &amp; Overview</H2>
    <P>
      The Learning Institute for Empowerment, LLC (LIFE LLC), operating on the NEXENGINES technical platform, respects your privacy and is committed to protecting your personal and financial information. This Privacy Policy explains how we collect, use, store, and safeguard your data when you use our mobile and web applications (the &ldquo;Application&rdquo;), including when you interact with financial account aggregation tools powered by Plaid Inc. (&ldquo;Plaid&rdquo;).
    </P>

    <H2>2. Information We Collect</H2>
    <P>We collect information to provide, maintain, and improve our services, facilitate vocational trade training allowances, and streamline financial wellness workflows:</P>
    <List>
      <Li><strong className="text-nex-silver">Account &amp; Profile Information:</strong> Full name, contact details (email address, phone number), mailing address, user credentials, and multi-factor authentication (MFA) tokens.</Li>
      <Li><strong className="text-nex-silver">Financial Account Information via Plaid Link:</strong> When you connect your bank account using Plaid Link, we collect financial institution identifiers, account balances, routing/account numbers, and historical transaction records provided directly by your financial institution through Plaid.</Li>
      <Li><strong className="text-nex-silver">Technical &amp; Usage Data:</strong> Device identifiers, IP addresses, log files, system activity, and interaction timestamps captured via the NEXENGINES operational ledger.</Li>
    </List>
    <Callout>
      <strong className="text-nex-gold uppercase mono-body text-[10px] tracking-widest">Plaid Data Integration Safeguard: </strong>
      LIFE LLC uses Plaid Link to connect your financial accounts securely. We never store or access your bank login credentials (usernames/passwords). Plaid processes your financial login credentials in accordance with Plaid&apos;s Privacy Policy.
    </Callout>

    <H2>3. How We Use Your Information</H2>
    <P>Your data is processed strictly for legitimate operational and compliance purposes, including:</P>
    <List>
      <Li>Verifying account ownership and validating identity prior to financial account linking.</Li>
      <Li>Automating participant allowance distributions, living support payments, and tuition/scholarship metrics.</Li>
      <Li>Mitigating fraud, preventing non-sufficient funds (NSF) returns, and protecting against unauthorized system access.</Li>
      <Li>Ensuring compliance with grant accounting, federal reporting, and state vocational standards.</Li>
    </List>

    <H2>4. Multi-Factor Authentication (MFA) &amp; Pre-Link Protection</H2>
    <P>
      To prevent unauthorized access to your account or third-party financial linking tools, LIFE LLC enforces step-up Multi-Factor Authentication (MFA via SMS OTP or TOTP Authenticator) directly on the Application before the Plaid Link interface is surfaced to the user. This ensures that only authenticated account holders can initiate financial connections.
    </P>

    <H2>5. Data Security &amp; Storage Standards</H2>
    <P>We implement robust, industry-standard security controls engineered directly into the NEXENGINES platform:</P>
    <List>
      <Li><strong className="text-nex-silver">Data Encryption:</strong> All data stored at rest is encrypted using AES-256 bit encryption. All data in transit across networks or APIs is protected via TLS 1.3 protocol.</Li>
      <Li><strong className="text-nex-silver">Access Controls:</strong> System access is governed by strict Role-Based Access Control (RBAC) and the Principle of Least Privilege. Only authorized personnel with an operational need can access system data.</Li>
      <Li><strong className="text-nex-silver">Network Isolation:</strong> Production databases are hosted in isolated Virtual Private Clouds (VPCs) with continuous audit logging.</Li>
    </List>

    <H2>6. Information Sharing &amp; Disclosure</H2>
    <P>LIFE LLC does not sell, rent, or trade your personal or financial information to third parties or advertisers. We share your data only in the following limited circumstances:</P>
    <List>
      <Li><strong className="text-nex-silver">Service Providers:</strong> Trusted technology partners (e.g., Plaid Inc. for financial connections, Stripe for payment processing, NEXENGINES for ERP infrastructure) operating under strict confidentiality and security mandates.</Li>
      <Li><strong className="text-nex-silver">Legal &amp; Regulatory Obligations:</strong> When required by law, court order, regulatory audit, or to enforce our contractual terms and protect user safety.</Li>
    </List>

    <H2>7. Your Privacy Rights &amp; Data Retention</H2>
    <P>
      Depending on your jurisdiction, you have rights regarding your personal information, including the right to access, correct, or request deletion of your stored data. You may also disconnect your linked bank account at any time through the Application settings, which revokes Plaid&apos;s active token connection.
    </P>

    <H2>8. Contact &amp; Privacy Inquiries</H2>
    <P><strong className="text-nex-silver">Privacy Officer &amp; Data Compliance Contact:</strong></P>
    <div className="card-industrial corner-tick p-5 my-6">
      <div className="text-nex-silver font-semibold mb-1">Learning Institute for Empowerment, LLC</div>
      <div className="text-nex-chrome text-sm">Attn: Privacy &amp; Information Security Department</div>
      <div className="text-nex-chrome text-sm">7433 Springside Drive, Myrtle Beach, SC 29588</div>
      <div className="text-nex-chrome text-sm mt-2">
        Email: <a href="mailto:will.smith@hoh2life.com" className="text-nex-gold hover:underline">will.smith@hoh2life.com</a>
      </div>
      <div className="text-nex-chrome text-sm">Phone: (843) 424-2655</div>
    </div>
    <P className="mono-body text-[11px] text-nex-muted">
      Technical Infrastructure Partner: NEXENGINES (nexengines.com)
    </P>
  </>
);

export default PrivacyPage;
